import { z } from 'zod';

import { ContractType, ManagerialAccountingQuarter } from '@/common/graphql';
import { periodOrd, QUARTERS_IN_ORDER, quarterForMonth } from '../core/managerial-accounting-quarter';

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth();
const currentOrd = periodOrd(currentYear, quarterForMonth(currentMonth));
// Same real-world time horizon as before the quarter cutover (4.5 years back, 1 year forward),
// scaled from half-year counts (-9/+2) to quarter counts (x2) per research.md #3's precedent.
const vdStartOrd = currentOrd - 18;
const vdEndOrd = currentOrd + 4;

export const VdYearQuarterFilterDefaults = {
  yearStart: String(Math.floor(vdStartOrd / 4)),
  quarterStart: QUARTERS_IN_ORDER[vdStartOrd % 4],
  yearEnd: String(Math.floor(vdEndOrd / 4)),
  quarterEnd: QUARTERS_IN_ORDER[vdEndOrd % 4],
} as const;

export const VdYearQuarterFilter = z.object({
  yearStart: z.string().optional().default(VdYearQuarterFilterDefaults.yearStart),
  quarterStart: z.nativeEnum(ManagerialAccountingQuarter).optional().default(VdYearQuarterFilterDefaults.quarterStart),
  yearEnd: z.string().optional().default(VdYearQuarterFilterDefaults.yearEnd),
  quarterEnd: z.nativeEnum(ManagerialAccountingQuarter).optional().default(VdYearQuarterFilterDefaults.quarterEnd),
});

export type VdYearQuarterFilterType = z.infer<typeof VdYearQuarterFilter>;

export enum ReportNumberOfEmployeesTab {
  ALL = 'all',
  COMPANY = 'company',
  OFFICE_LOCATION = 'office_location',
  BUSINESS_UNIT = 'business_unit',
  JOB = 'job',
  COMPANY__BUSINESS_UNIT__JOB = 'company-business_unit-job',
  COMPANY__BUSINESS_UNIT__DEPARTMENT = 'company-business_unit-department',
  NATIONALITY = 'nationality',
  TURNOVER = 'turnover',
}

export const NumberOfEmployeesFilter = z
  .object({
    startYearMonth: z
      .string()
      .optional()
      .default(() => `${new Date().getFullYear()}-01`),
    endYearMonth: z
      .string()
      .optional()
      .default(() => `${new Date().getFullYear()}-12`),
    contractTypes: z.array(z.nativeEnum(ContractType)).catch([]),
  })
  .refine(data => data.startYearMonth <= data.endYearMonth, {
    message: 'End must be on or after Start',
    path: ['endYearMonth'],
  });

export type NumberOfEmployeesFilterType = z.infer<typeof NumberOfEmployeesFilter>;
